export const TASK_EDITOR_EDITABLE_BLOCK_TYPES = [
  "Instruction",
  "Header1",
] as const;

export const TASK_EDITOR_EDITABLE_LINE_TYPES = ["TypographyLine"] as const;
export const TASK_EDITOR_EDITABLE_CELL_TYPES = [
  "Typography",
  "Textarea",
  "Image",
  "ButtonGroup",
  "ButtonSelection",
  "Quiz",
] as const;
export const TASK_EDITOR_TYPOGRAPHY_STYLES = [
  "Body",
  "Label",
  "Alert",
  "Dimmed",
] as const;

export const TASK_EDITOR_SPECIAL_BLOCK_TYPES = ["LastCard"] as const;

export const TASK_EDITOR_BLOCK_TYPES = [
  ...TASK_EDITOR_EDITABLE_BLOCK_TYPES,
  ...TASK_EDITOR_SPECIAL_BLOCK_TYPES,
] as const;

export const TASK_EDITOR_LAST_CARD_TYPE = "LastCard";
export const TASK_EDITOR_LAST_CARD_TRANSLATION_KEYS = [
  "common:lastCard1",
  "common:lastCard2",
] as const;
export const TASK_EDITOR_LAST_CARD_LABEL =
  TASK_EDITOR_LAST_CARD_TRANSLATION_KEYS.join("\n");
export const TASK_EDITOR_QUIZ_DEFAULT_INCORRECT_MESSAGE = "다시 생각해보세요";
const TASK_EDITOR_LAST_CARD_BLOCK_ID = "task-editor-last-card";
const TASK_EDITOR_NESTED_BLOCK_TYPES = ["Instruction"] as const;

export type TaskEditorBlockType = (typeof TASK_EDITOR_BLOCK_TYPES)[number];
export type TaskEditorLineType =
  (typeof TASK_EDITOR_EDITABLE_LINE_TYPES)[number];
export type TaskEditorCellType =
  (typeof TASK_EDITOR_EDITABLE_CELL_TYPES)[number];
export type TaskEditorTypographyStyle =
  (typeof TASK_EDITOR_TYPOGRAPHY_STYLES)[number];

export interface TaskEditorButtonGroupOption {
  text: string;
  showFromId?: string;
  showToId?: string;
  restartFromId?: string;
  hideFromId?: string;
  isCorrect?: boolean;
}

export interface TaskEditorCell {
  id: string;
  type: TaskEditorCellType;
  content: string;
  width?: number;
  options?: TaskEditorButtonGroupOption[];
  textareaPlaceholder?: string;
  incorrectMessage?: string;
  allowMultipleSelection?: boolean;
}

export interface TaskEditorLine {
  id: string;
  type: TaskEditorCellType;
  content: string;
  options?: TaskEditorButtonGroupOption[];
  typographyStyle?: TaskEditorTypographyStyle;
  textareaPlaceholder?: string;
  incorrectMessage?: string;
  allowMultipleSelection?: boolean;
}

export interface TaskEditorBlock {
  id: string;
  type: TaskEditorBlockType;
  content: string;
  lines?: TaskEditorLine[];
}

export interface TaskEditorState {
  taskId: string;
  title: string;
  setNumber?: number;
  isKeyActivity?: boolean;
  blocks: TaskEditorBlock[];
}

type TaskEditorCompatibleBlockType = TaskEditorBlockType;

function createTaskEditorElementId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
}

export function createTaskEditorButtonGroupOption(
  value?: Partial<TaskEditorButtonGroupOption>
): TaskEditorButtonGroupOption {
  return {
    text: typeof value?.text === "string" ? value.text : "",
    ...(typeof value?.showFromId === "string"
      ? { showFromId: value.showFromId }
      : {}),
    ...(typeof value?.showToId === "string" ? { showToId: value.showToId } : {}),
    ...(typeof value?.restartFromId === "string"
      ? { restartFromId: value.restartFromId }
      : {}),
    ...(typeof value?.hideFromId === "string"
      ? { hideFromId: value.hideFromId }
      : {}),
    ...(value?.isCorrect === true ? { isCorrect: true } : {}),
  };
}

export function getTaskEditorBlockTypeLabel(
  type: TaskEditorCompatibleBlockType | string
) {
  switch (type) {
    case "Instruction":
    case "Content":
      return "Body";
    case "Header1":
      return "Header";
    default:
      return type;
  }
}

export function isTaskEditorNestedBlockType(
  type: TaskEditorCompatibleBlockType | string
) {
  return ([...TASK_EDITOR_NESTED_BLOCK_TYPES] as readonly string[]).includes(
    type
  );
}

export function getTaskEditorAvailableLineTypes(
  type: TaskEditorCompatibleBlockType | string
) {
  return isTaskEditorNestedBlockType(type)
    ? [...TASK_EDITOR_EDITABLE_LINE_TYPES]
    : [];
}

export function getDefaultTaskEditorLineType(
  type: TaskEditorCompatibleBlockType | string
): TaskEditorLineType | undefined {
  const availableTypes = getTaskEditorAvailableLineTypes(type);
  return availableTypes.length > 0 ? availableTypes[0] : undefined;
}

export function getTaskEditorLineTypeLabel(type: TaskEditorLineType | string) {
  switch (type) {
    case "TypographyLine":
      return "컴포넌트";
    default:
      return `Legacy${type}`;
  }
}

export function getTaskEditorCellTypeLabel(type: TaskEditorCellType) {
  switch (type) {
    case "Typography":
      return "Text";
    case "Textarea":
      return "Text Input";
    case "Image":
      return "Image";
    case "ButtonGroup":
      return "Branching Buttons";
    case "ButtonSelection":
      return "Button Selection";
    case "Quiz":
      return "Quiz";
    default:
      return type;
  }
}

export function getTaskEditorTypographyStyleLabel(
  style: TaskEditorTypographyStyle
) {
  switch (style) {
    case "Label":
      return "label";
    case "Alert":
      return "alert";
    case "Dimmed":
      return "dimmed";
    default:
      return "body";
  }
}

export function createTaskEditorCell(
  type: TaskEditorCellType = "Typography"
): TaskEditorCell {
  const hasButtonOptions =
    type === "ButtonGroup" || type === "ButtonSelection" || type === "Quiz";
  const isQuiz = type === "Quiz";
  const isButtonSelection = type === "ButtonSelection";
  const isTextarea = type === "Textarea";

  return {
    id: createTaskEditorElementId("task-editor-cell"),
    type,
    content: "",
    width: 1,
    ...(hasButtonOptions
      ? {
          options: [createTaskEditorButtonGroupOption(), createTaskEditorButtonGroupOption()],
        }
      : {}),
    ...(isTextarea
      ? {
          textareaPlaceholder: "",
        }
      : {}),
    ...(isQuiz
      ? {
          incorrectMessage: "",
        }
      : {}),
    ...(isButtonSelection
      ? {
          allowMultipleSelection: true,
        }
      : {}),
  };
}

export function createTaskEditorLine(
  type: TaskEditorCellType = "Typography"
): TaskEditorLine {
  const hasButtonOptions =
    type === "ButtonGroup" || type === "ButtonSelection" || type === "Quiz";
  const isQuiz = type === "Quiz";
  const isButtonSelection = type === "ButtonSelection";
  const isTypography = type === "Typography";
  const isTextarea = type === "Textarea";

  return {
    id: createTaskEditorElementId("task-editor-line"),
    type,
    content: "",
    ...(hasButtonOptions
      ? {
          options: [createTaskEditorButtonGroupOption(), createTaskEditorButtonGroupOption()],
        }
      : {}),
    ...(isTypography
        ? {
            typographyStyle: "Body" as TaskEditorTypographyStyle,
          }
        : {}),
    ...(isTextarea
      ? {
          textareaPlaceholder: "",
        }
      : {}),
    ...(isQuiz
      ? {
          incorrectMessage: "",
        }
      : {}),
    ...(isButtonSelection
      ? {
          allowMultipleSelection: true,
        }
      : {}),
  };
}

export function createTaskEditorBlock(
  type: TaskEditorBlockType = "Instruction"
): TaskEditorBlock {
  if (type === TASK_EDITOR_LAST_CARD_TYPE) {
    return createTaskEditorLastCardBlock();
  }

  return {
    id: createTaskEditorElementId("task-editor-block"),
    type,
    content: "",
    ...(isTaskEditorNestedBlockType(type)
      ? {
          lines: [createTaskEditorLine()],
        }
      : {}),
  };
}

export function createTaskEditorLastCardBlock(): TaskEditorBlock {
  return {
    id: TASK_EDITOR_LAST_CARD_BLOCK_ID,
    type: TASK_EDITOR_LAST_CARD_TYPE,
    content: TASK_EDITOR_LAST_CARD_LABEL,
  };
}

export function isTaskEditorLastCardBlock(block: TaskEditorBlock) {
  return block.type === TASK_EDITOR_LAST_CARD_TYPE;
}

export function ensureTaskEditorLastCard(
  blocks: TaskEditorBlock[]
): TaskEditorBlock[] {
  const blocksWithoutLastCard = blocks.filter(
    (block) => !isTaskEditorLastCardBlock(block)
  );
  const lastCardBlock = blocks.find(isTaskEditorLastCardBlock);

  return [
    ...blocksWithoutLastCard,
    {
      ...(lastCardBlock ?? createTaskEditorLastCardBlock()),
      id: TASK_EDITOR_LAST_CARD_BLOCK_ID,
      type: TASK_EDITOR_LAST_CARD_TYPE,
      content: TASK_EDITOR_LAST_CARD_LABEL,
    },
  ];
}

export function moveBlock(
  blocks: TaskEditorBlock[],
  index: number,
  direction: "up" | "down"
) {
  const targetIndex = direction === "up" ? index - 1 : index + 1;

  return reorderBlocks(blocks, index, targetIndex);
}

export function reorderBlocks(
  blocks: TaskEditorBlock[],
  fromIndex: number,
  toIndex: number
) {
  if (
    fromIndex < 0 ||
    fromIndex >= blocks.length ||
    toIndex < 0 ||
    toIndex >= blocks.length ||
    fromIndex === toIndex
  ) {
    return blocks;
  }

  const nextBlocks = [...blocks];
  const [targetBlock] = nextBlocks.splice(fromIndex, 1);
  nextBlocks.splice(toIndex, 0, targetBlock);

  return nextBlocks;
}

export function reorderLines(
  lines: TaskEditorLine[],
  fromIndex: number,
  toIndex: number
) {
  if (
    fromIndex < 0 ||
    fromIndex >= lines.length ||
    toIndex < 0 ||
    toIndex >= lines.length ||
    fromIndex === toIndex
  ) {
    return lines;
  }

  const nextLines = [...lines];
  const [targetLine] = nextLines.splice(fromIndex, 1);
  nextLines.splice(toIndex, 0, targetLine);

  return nextLines;
}

function escapeContent(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function getContentVariableName(taskId: string) {
  const fallback = "NewTask";
  const baseName = taskId.trim() || fallback;

  return `Content_${baseName.replace(/[^a-zA-Z0-9]/g, "_")}`;
}

function getNormalizedBlockLines(block: TaskEditorBlock) {
  return block.lines && block.lines.length > 0
    ? block.lines
    : [createTaskEditorLine()];
}

function isMeaningfulTaskEditorLine(line: TaskEditorLine) {
  if (line.type === "Typography" || line.type === "Image") {
    return Boolean(line.content.trim());
  }

  return true;
}

function hasMeaningfulTaskEditorLines(block: TaskEditorBlock) {
  return getNormalizedBlockLines(block).some((line) =>
    isMeaningfulTaskEditorLine(line)
  );
}

function getUsedBlockTypes(blocks: TaskEditorBlock[]) {
  return Array.from(
    new Set(
      blocks.flatMap((block) => {
        if (isTaskEditorLastCardBlock(block)) {
          return [];
        }

        if (
          isTaskEditorNestedBlockType(block.type) &&
          hasMeaningfulTaskEditorLines(block)
        ) {
          return [];
        }

        return [block.type];
      })
    )
  );
}

function getUsedLineTypes(blocks: TaskEditorBlock[]) {
  const usedLineTypes = new Set<TaskEditorLineType>();

  blocks.forEach((block) => {
    if (!isTaskEditorNestedBlockType(block.type)) {
      return;
    }

    const lines = getNormalizedBlockLines(block);

    if (block.type === "Instruction" && hasMeaningfulTaskEditorLines(block)) {
      usedLineTypes.add("TypographyLine");
    }

    lines.forEach((line) => {
      if (line.type === "Typography") {
        usedLineTypes.add("TypographyLine");
      }
    });
  });

  return Array.from(usedLineTypes);
}

function getUsedCellTypes(blocks: TaskEditorBlock[]) {
  return Array.from(
    new Set(
      blocks.flatMap((block) =>
        isTaskEditorNestedBlockType(block.type)
          ? getNormalizedBlockLines(block)
              .filter((line) => line.type !== "Typography")
              .map((line) =>
                line.type === "Quiz" || line.type === "ButtonSelection"
                  ? "ButtonGroupMultipleChoice"
                  : line.type
              )
          : []
      )
    )
  );
}

function getImportLines(blocks: TaskEditorBlock[]) {
  const usedBlockTypes = getUsedBlockTypes(blocks);
  const usedLineTypes = getUsedLineTypes(blocks);
  const usedCellTypes = getUsedCellTypes(blocks);
  const importLines: string[] = [];

  if (usedBlockTypes.length) {
    importLines.push(
      `import { ${usedBlockTypes.join(", ")} } from "../../data/BlockComponentV2";`
    );
  }

  if (blocks.some(isTaskEditorLastCardBlock)) {
    importLines.push(`import { LastCard } from "../../data/task/common";`);
  }

  if (usedLineTypes.length || usedCellTypes.length) {
    importLines.push(
      `import { ${[...usedLineTypes, ...usedCellTypes].join(", ")} } from "../../data/CellComponentV2";`
    );
  }

  return importLines;
}

function getButtonGroupOptionCode(
  option: TaskEditorButtonGroupOption,
  includeBranchIds = true
) {
  const optionText = escapeContent((option.text ?? "").trim());
  const normalizedShowFromId = (option.showFromId ?? "").trim();
  const normalizedShowToId = (option.showToId ?? "").trim();
  const normalizedRestartFromId = (option.restartFromId ?? "").trim();
  const normalizedHideFromId = (option.hideFromId ?? "").trim();

  return [
    "{",
    `text: "${optionText}"`,
    ...(includeBranchIds && normalizedShowFromId.length > 0
      ? [`, showFromId: "${escapeContent(normalizedShowFromId)}"`]
      : []),
    ...(includeBranchIds && normalizedShowToId.length > 0
      ? [`, showToId: "${escapeContent(normalizedShowToId)}"`]
      : []),
    ...(includeBranchIds && normalizedRestartFromId.length > 0
      ? [`, restartFromId: "${escapeContent(normalizedRestartFromId)}"`]
      : []),
    ...(includeBranchIds && normalizedHideFromId.length > 0
      ? [`, hideFromId: "${escapeContent(normalizedHideFromId)}"`]
      : []),
    "}",
  ].join("");
}

function getCellCode(cell: TaskEditorCell) {
  switch (cell.type) {
    case "ButtonGroup": {
      const options = (
        cell.options ?? [
          createTaskEditorButtonGroupOption(),
          createTaskEditorButtonGroupOption(),
        ]
      ).map((option) => `          ${getButtonGroupOptionCode(option)}`);

      return [
        "ButtonGroup(",
        "        [",
        options.join(",\n"),
        "        ],",
        `        { id: "${escapeContent(cell.content.trim())}" }`,
        "      )",
      ].join("\n");
    }
    case "Quiz": {
      const normalizedOptions = (
        cell.options ?? [
          createTaskEditorButtonGroupOption(),
          createTaskEditorButtonGroupOption(),
        ]
      ).map((option) => createTaskEditorButtonGroupOption(option));
      const options = normalizedOptions.map(
        (option) => `          ${getButtonGroupOptionCode(option, false)}`
      );
      const correctOptionIndices = normalizedOptions.flatMap((option, index) =>
        option.isCorrect === true ? [index] : []
      );
      const normalizedIncorrectMessage = (cell.incorrectMessage ?? "").trim();
      const quizCellArgs = [
        `id: "${escapeContent(cell.content.trim())}"`,
        ...(correctOptionIndices.length > 0
          ? [
              `correctOptionIndices: [${correctOptionIndices.join(", ")}]`,
              `requiredSelectionCount: ${correctOptionIndices.length}`,
            ]
          : []),
        ...(normalizedIncorrectMessage.length > 0
          ? [
              `incorrectMessage: "${escapeContent(normalizedIncorrectMessage)}"`,
            ]
          : []),
      ];

      return [
        "ButtonGroupMultipleChoice(",
        "        [",
        options.join(",\n"),
        "        ],",
        `        { ${quizCellArgs.join(", ")} }`,
        "      )",
      ].join("\n");
    }
    case "ButtonSelection": {
      const normalizedOptions = (
        cell.options ?? [
          createTaskEditorButtonGroupOption(),
          createTaskEditorButtonGroupOption(),
        ]
      ).map((option) => createTaskEditorButtonGroupOption(option));
      const options = normalizedOptions.map(
        (option) => `          ${getButtonGroupOptionCode(option, false)}`
      );
      const allowsMultipleSelection = cell.allowMultipleSelection !== false;
      const selectionCellArgs = [
        `id: "${escapeContent(cell.content.trim())}"`,
        ...(allowsMultipleSelection ? [] : ["requiredSelectionCount: 1"]),
      ];

      return [
        "ButtonGroupMultipleChoice(",
        "        [",
        options.join(",\n"),
        "        ],",
        `        { ${selectionCellArgs.join(", ")} }`,
        "      )",
      ].join("\n");
    }
    case "Textarea": {
      const textareaId = escapeContent(cell.content.trim());
      const textareaPlaceholder = (cell.textareaPlaceholder ?? "").trim();

      return textareaPlaceholder.length > 0
        ? `Textarea({ id: "${textareaId}", placeholder: "${escapeContent(
            textareaPlaceholder
          )}" })`
        : `Textarea({ id: "${textareaId}" })`;
    }
    case "Image":
      return `Image("${escapeContent(cell.content.trim())}")`;
    default:
      return `Typography("${escapeContent(cell.content.trim())}")`;
  }
}

function getLineCode(line: TaskEditorLine) {
  if (line.type === "Typography") {
    const typographyArg =
      line.typographyStyle === "Label"
        ? ", { isLabel: true }"
        : line.typographyStyle === "Alert"
          ? ", { isAlert: true }"
          : line.typographyStyle === "Dimmed"
            ? ", { isDimmed: true }"
            : "";

    return `TypographyLine("${escapeContent(line.content.trim())}"${typographyArg})`;
  }

  const cell: TaskEditorCell = {
    ...createTaskEditorCell(line.type),
    id: line.id,
    content: line.content,
    ...(line.type === "ButtonGroup"
      ? {
          options:
            line.options && line.options.length > 0
              ? line.options.map((option) =>
                  createTaskEditorButtonGroupOption(option)
                )
              : [
                  createTaskEditorButtonGroupOption(),
                  createTaskEditorButtonGroupOption(),
                ],
        }
      : {}),
    ...(line.type === "Quiz"
      ? {
          options:
            line.options && line.options.length > 0
              ? line.options.map((option) =>
                  createTaskEditorButtonGroupOption(option)
                )
              : [
                  createTaskEditorButtonGroupOption(),
                  createTaskEditorButtonGroupOption(),
                ],
          incorrectMessage: line.incorrectMessage ?? "",
        }
      : {}),
    ...(line.type === "ButtonSelection"
      ? {
          options:
            line.options && line.options.length > 0
              ? line.options.map((option) =>
                  createTaskEditorButtonGroupOption(option)
                )
              : [
                  createTaskEditorButtonGroupOption(),
                  createTaskEditorButtonGroupOption(),
                ],
          allowMultipleSelection: line.allowMultipleSelection !== false,
        }
      : {}),
    ...(line.type === "Textarea"
      ? {
          textareaPlaceholder: line.textareaPlaceholder ?? "",
        }
      : {}),
  };

  return `[${getCellCode(cell)}]`;
}

function getContentBlockCode(block: TaskEditorBlock) {
  const lines = getNormalizedBlockLines(block).filter((line) =>
    isMeaningfulTaskEditorLine(line)
  );

  if (!lines.length) {
    return `Instruction("${escapeContent(block.content.trim())}")`;
  }

  const contentLines = [...lines.map((line) => getLineCode(line))];

  return [
    "{",
    "    lines: [",
    ...contentLines.map((line) => `      ${line},`),
    "    ],",
    "  }",
  ].join("\n");
}

function getBlockCode(block: TaskEditorBlock) {
  if (isTaskEditorLastCardBlock(block)) {
    return "...LastCard";
  }

  if (isTaskEditorNestedBlockType(block.type)) {
    return getContentBlockCode(block);
  }

  const content = escapeContent(block.content.trim());
  return `${block.type}("${content}")`;
}

export function generateTaskEditorSource({
  taskId,
  title,
  setNumber,
  isKeyActivity,
  blocks,
}: TaskEditorState) {
  const normalizedBlocks = blocks.length ? blocks : [createTaskEditorBlock()];
  const importLines = getImportLines(normalizedBlocks);
  const variableName = getContentVariableName(taskId);
  const normalizedTaskId = taskId.trim() || "NEW-TASK-ID";
  const normalizedTitle = title.trim() || "새 Task 제목";
  const normalizedSetNumber =
    typeof setNumber === "number" && Number.isFinite(setNumber) && setNumber > 0
      ? Math.floor(setNumber)
      : undefined;

  const contentCode = normalizedBlocks
    .map((block) => `  ${getBlockCode(block)},`)
    .join("\n");

  return [
    ...importLines,
    "",
    `export const ${variableName} = [`,
    contentCode,
    "];",
    "",
    "// programData.ts 또는 task 메타데이터 등록용 예시",
    "{",
    `  taskId: \"${escapeContent(normalizedTaskId)}\",`,
    `  title: \"${escapeContent(normalizedTitle)}\",`,
    ...(normalizedSetNumber !== undefined
      ? [`  setNumber: ${normalizedSetNumber},`]
      : []),
    ...(isKeyActivity ? ["  isKeyActivity: true,"] : []),
    `  content: ${variableName},`,
    "},",
  ].join("\n");
}
